import type { VaultMediaItemDto } from '@api'
import { randUuid } from '@ngneat/falso'
import axios from 'axios'
import { Domain, attach, sample } from 'effector'

import { $creatorId } from '@/models/creators'
import { $cloneToOriginalRegistry } from '@/models/vault/medias/clonned.ts'
import { chatsService } from '@/modules/chatting/service/chats'
import { isUploadedFile } from '@/modules/chatting/ui/utils'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import {
  MediaItem,
  SetIsPreviewMediaOptions,
  UploadMediaInChunksFxOptions,
  UploadingMediaItem,
} from './types'

export const createUserEditorMediasModel = (
  userDomain: Domain,
  cloneMediaModel: ReturnType<
    typeof import('@/models/vault/medias/clonned').createUserCloneMediaModel
  >,
) => {
  const { createEvent, createStore, createEffect } = userDomain

  const cancelUploading = createEvent<UploadingMediaItem['id']>(
    'cancel media uploading',
  )
  const updateProgress = createEvent<
    Pick<UploadingMediaItem, 'id' | 'progress'>
  >('update uploading media progress')

  const setMedias = createEvent<MediaItem[]>(
    "set uploaded or vault medias' list",
  )
  const removeMedia = createEvent<MediaItem>('remove uploaded or vault media')
  const setIsPreviewMedia = createEvent<SetIsPreviewMediaOptions>(
    'change media preview mode',
  )
  const addVaultMedias = createEvent<StreamableVaultMedia[]>(
    "add vault medias to medias' list",
  )

  const uploadMediaInChunksFx = createEffect(
    async ({
      creatorId,
      file,
      id,
      cancelSource,
    }: UploadMediaInChunksFxOptions) =>
      chatsService.uploadMediaInChunks({
        creatorId,
        file,
        fileName: file.name,
        cancelToken: cancelSource.token,
        onUploadProgress: ({ loaded, total }) => {
          const progress = Math.round((loaded * 100) / (total || 100))

          updateProgress({ id, progress })
        },
      }),
  )

  uploadMediaInChunksFx.failData.watch((error) => {
    if (!(error instanceof axios.Cancel)) {
      handleError(error, 'Failed to upload media, please try again')
    }
  })

  const $medias = createStore<MediaItem[]>([], { name: 'uploaded medias' })
    .on(uploadMediaInChunksFx.doneData, (medias, addingMedia) => {
      return [...medias, addingMedia]
    })
    .on(setMedias, (_, value) => {
      return value.filter(Boolean)
    })
    .on(setIsPreviewMedia, (medias, { index, isPreview }) => {
      return medias.map((media, i) =>
        i === index
          ? {
              ...media,
              isPreview,
            }
          : media,
      )
    })
    .on(addVaultMedias, (medias, addingMedias) => {
      const newMedias = addingMedias
        .filter(Boolean)
        .filter(
          (addingMedia) =>
            !medias
              .filter(Boolean)
              .some(
                (media) =>
                  !isUploadedFile(media) && media.id === addingMedia.id,
              ),
        )

      return [...medias, ...newMedias]
    })

  const $uploadingMedias = createStore<UploadingMediaItem[]>([], {
    name: 'uploading medias',
  })
    .on(
      uploadMediaInChunksFx,
      (uploadingMedias, { id, file, cancelSource }) => {
        return [
          ...uploadingMedias,
          {
            id,
            file,
            isUploading: true,
            cancelSource,
            progress: 0,
          },
        ]
      },
    )
    .on(updateProgress, (uploadingMedias, { id, progress }) => {
      return uploadingMedias.map((uploadingMedia) => {
        if (uploadingMedia.id === id) {
          return {
            ...uploadingMedia,
            progress,
          }
        }

        return uploadingMedia
      })
    })
    .on(
      [uploadMediaInChunksFx.done, uploadMediaInChunksFx.fail],
      (uploadingMedias, { params: { id: removingId } }) => {
        return uploadingMedias.filter(
          (uploadingMedia) => uploadingMedia.id !== removingId,
        )
      },
    )
    .on(cancelUploading, (uploadingMedias, cancellingId) => {
      const uploadingMedia = uploadingMedias.find(
        ({ id }) => id === cancellingId,
      )

      if (uploadingMedia) {
        uploadingMedia.cancelSource.cancel('Upload cancelled by user')
      }

      return uploadingMedias.filter(
        (uploadingMedia) => uploadingMedia.id !== cancellingId,
      )
    })

  const uploadMediaFx = attach({
    source: { creatorId: $creatorId, uploadingMedias: $uploadingMedias },
    effect: uploadMediaInChunksFx,
    mapParams: (file: File, { creatorId, uploadingMedias }) => ({
      creatorId: creatorId!,
      uploadingMedias,
      file,
      id: randUuid(),
      cancelSource: axios.CancelToken.source(),
    }),
  })

  sample({
    clock: removeMedia,
    source: {
      medias: $medias,
    },
    fn: ({ medias }, removingMedia) => {
      if (isUploadedFile(removingMedia)) {
        return medias.filter(
          (media) =>
            !isUploadedFile(media) ||
            media.processId !== removingMedia.processId,
        )
      } else {
        return medias.filter(
          (media) => isUploadedFile(media) || media.id !== removingMedia.id,
        )
      }
    },
    target: $medias,
  })

  sample({
    clock: removeMedia,
    source: $cloneToOriginalRegistry,
    filter: (registry, media) => {
      if (isUploadedFile(media)) {
        return false
      }

      return registry.has(media.id)
    },
    fn: (_, media) => (isUploadedFile(media) ? 0 : media.id),
    target: cloneMediaModel.events.removeCloneByMediaId,
  })

  sample({
    source: cloneMediaModel.stores.$cloneMediaJobsFinished,
    filter: (finishedJobs) => finishedJobs.length > 0,
    fn: (jobs) =>
      jobs
        .map((job) => job.media)
        .filter((media): media is VaultMediaItemDto =>
          isTruthyAndNotEmpty(media),
        ),
    target: addVaultMedias,
  })

  return {
    events: {
      uploadMediaFx,
      cancelUploading,
      setMedias,
      removeMedia,
      setIsPreviewMedia,
      addVaultMedias,
    },
    stores: {
      $medias,
      $uploadingMedias,
    },
  }
}
